import { ReactElement, useMemo } from "react";
import { Navigate,useLocation, Outlet, RouterProvider, createBrowserRouter, RouteObject } from 'react-router-dom';
import { LoginPage } from 'components/views/login/LoginPage';
import { UserProfileView } from "components/views/profile/UserProfileView";
import { RoutePaths } from "constants/RoutePaths";
import { ForgotPasswordPage } from "components/views/forgotPassword/ForgotPasswordPage";
import { FinishRegistrationPage } from "components/views/registration/FinishRegistrationPage/FinishRegistrationPage";
import { FinishRegistrationPageMobile } from "components/views/mobile/finish-registration/FinishRegistrationPageMobile";
import { ResetPasswordPage } from "components/views/resetPassword/ResetPasswordPage";
import { UnauthorizedPage } from "components/views/401/UnauthorizedPage";
import { EmailConfirmationPage } from "components/views/emailConfirm/EmailConfirmationPage";
import { NavbarComponent } from "components/NavbarComponent/NavbarComponent";
import { MyTrainingsPage } from "components/views/myTrainings/MyTrainingsPage";
import { MobileHomePage } from "components/views/mobile/MobileHomePage";
import { MobileQrPage } from "components/views/mobile/MobileQrPage";
import { UserProfileViewMobile } from "components/views/mobile/UserProfileViewMobile";
import { TrainingsManagementPage } from "components/views/trainingsManagement/TrainingsManagementPage/TrainingsManagementPage";
import { UsersManagementPage } from "components/views/usersManagement/UsersManagementPage/UsersManagementPage";
import { StatisticsPage } from "components/views/statistics/StatisticsPage/StatisticsPage";
import { LivePage } from "components/views/live/LivePage/LivePage";
import { AddTrainingPage } from "components/views/editor/AddTrainingPage";
import { UpdateTrainingPage } from "components/views/editor/UpdateTrainingPage";
import { InvitationErrorPage } from "components/views/InvitationErrorPage/InvitationErrorPage";
import { ResetPasswordErrorPage } from "components/views/resetPasswordErrorPage/ResetPasswordErrorPage";
import { MfaPage } from "components/views/mfa/MfaPage";
import { WorkspaceManagementPage } from "components/views/workspace/WorkspaceManagementPage";
import { ContentOfRegulationsPage } from "./views/contentOfRegulations/ContentOfRegulationsPage";
import { PrivacyPolicyPage } from "./views/privacyPolicy/PrivacyPolicyPage";
import { useIsMobile } from "hooks/useIsMobile";

function HomeRoute() {
    const isMobile = useIsMobile();
    return isMobile ? <MobileHomePage /> : <MyTrainingsPage />;
}

function ProfileRoute() {
    const isMobile = useIsMobile();
    return isMobile ? <UserProfileViewMobile /> : <UserProfileView />;
}

// replace it by component from another MR
function FinishRegistrationRoute() {
    const isMobile = useIsMobile();
    return isMobile ? <FinishRegistrationPageMobile /> : <FinishRegistrationPage />;
}

function RoutesWithConditionalNavbar() {
    const location = useLocation();
    const isMobile = useIsMobile();
    const showNavbar = useMemo(() => {
        const pathsHidingNavbar = [
            RoutePaths.LOGIN,
            RoutePaths.FORGOT_PASSWORD,
            RoutePaths.RESET_PASSWORD,
            RoutePaths.FINISH_REGISTRATION,
            RoutePaths.CONFIRM_EMAIL,
            RoutePaths.UNAUTHORIZED,
            RoutePaths.MFA,
            RoutePaths.PRIVACY_POLICY,
            RoutePaths.CONTENT_OF_REGULATIONS
        ];
        return !pathsHidingNavbar.includes(location.pathname as RoutePaths);
    }, [location.pathname]);

    return (
        <>
            {!isMobile && showNavbar && <NavbarComponent />}
            <Outlet />
        </>
    );
}

const routes: RouteObject[] = [
    {
        path: '/',
        element: <RoutesWithConditionalNavbar />,
        children: [
            { path: RoutePaths.LOGIN, element: <LoginPage /> },
            { path: RoutePaths.PROFILE, element: <ProfileRoute /> },
            { path: RoutePaths.HOME, element: <HomeRoute /> },
            { path: RoutePaths.HOME_QR, element: <MobileQrPage /> },
            { path: RoutePaths.TRAININGS, element: <TrainingsManagementPage /> },
            { path: RoutePaths.USERS_MANAGEMENT, element: <UsersManagementPage /> },
            { path: RoutePaths.STATISTICS, element: <StatisticsPage /> },
            { path: `${RoutePaths.STATISTICS}/lessons/:lessonId`, element: <StatisticsPage /> },
            { path: RoutePaths.EDITOR, element: <AddTrainingPage /> },
            { path: `${RoutePaths.EDITOR}/:id`, element: <UpdateTrainingPage /> },
            { path: `${RoutePaths.LIVE}/:lessonId`, element: <LivePage /> },
            { path: RoutePaths.LIVE, element: <LivePage /> },
            { path: RoutePaths.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
            { path: RoutePaths.RESET_PASSWORD, element: <ResetPasswordPage /> },
            { path: RoutePaths.FINISH_REGISTRATION, element: <FinishRegistrationRoute /> },
            { path: RoutePaths.CONFIRM_EMAIL, element: <EmailConfirmationPage /> },
            { path: RoutePaths.UNAUTHORIZED, element: <UnauthorizedPage /> },
            { path: RoutePaths.INVITATION_ERROR, element: <InvitationErrorPage /> },
            { path: RoutePaths.RESET_PASSWORD_ERROR, element: <ResetPasswordErrorPage /> },
            { path: RoutePaths.MFA, element: <MfaPage /> },
            { path: RoutePaths.WORKSPACE_SETTINGS, element: <WorkspaceManagementPage /> },
            { path: RoutePaths.CONTENT_OF_REGULATIONS, element: <ContentOfRegulationsPage />},
            { path: RoutePaths.PRIVACY_POLICY, element: <PrivacyPolicyPage />},
            { path: '/', element: <Navigate replace to={RoutePaths.LOGIN} /> },
            { path: '*', element: <Navigate replace to={RoutePaths.LOGIN} /> }
        ],
    }
];

const router = createBrowserRouter(routes);

export function AppRoutes(): ReactElement {
    return <RouterProvider router={router} />;
}